import { useCallback, useState, type Dispatch, type SetStateAction } from "react";
import type { DailySeries } from "../model/dailySeries.ts";
import type { AquiferLayer, OutletPointLayer, SubBasinLayer } from "../model/gisLayer.ts";
import type { GenericLayer } from "../model/genericLayer.ts";
import type { LoadedProject } from "../model/project.ts";
import type { AemetProvenance } from "../model/storage.ts";
import type { EvalHidProject } from "../model/types.ts";
import type { ResultSelection } from "./resultSelection.ts";
import type { State } from "./appState.ts";

/**
 * The plain "replace this one piece of the open project" state transitions —
 * pulled out as named, exported functions (rather than staying as inline
 * `setState` closures) so they're directly unit-testable without a React
 * environment, same idea as the pure functions in `../engine/`/`../model/`.
 * Each is a one-line patch with no branching logic of its own, unlike the
 * other extracted groups (persistence, entity creation) which have real
 * decisions to make. Grouped together purely because they're all the same
 * shape, not because they share any deeper behavior — see
 * project_evalhid_code_audit.md for the ongoing App.tsx breakup this is
 * part of.
 */
export function withProject(current: State, project: EvalHidProject): State {
  return current.phase === "loaded"
    ? { ...current, loadedProject: { ...current.loadedProject, project } }
    : current;
}

export function withGisLayer(current: State, layer: SubBasinLayer): State {
  return current.phase === "loaded" ? { ...current, gisLayer: layer } : current;
}

/** Links the outlet points' own GIS layer — see OutletPointsPanel.tsx/LinkGisPanel.tsx. */
export function withOutletPointsLayer(current: State, layer: OutletPointLayer): State {
  return current.phase === "loaded" ? { ...current, outletPointsLayer: layer } : current;
}

/** Links the aquifers' own GIS layer — see AquifersPanel.tsx/LinkGisPanel.tsx. */
export function withAquifersLayer(current: State, layer: AquiferLayer): State {
  return current.phase === "loaded" ? { ...current, aquifersLayer: layer } : current;
}

/** Replaces the whole list of purely-visual reference layers — see GenericLayersPanel.tsx/genericLayer.ts. */
export function withGenericLayers(current: State, layers: GenericLayer[]): State {
  return current.phase === "loaded" ? { ...current, genericLayers: layers } : current;
}

/** Replaces the whole observed-flow series (EditOutletPointDialog's "Observado" tab, via PasteDailySeries.tsx) — same "replace the whole thing" contract as `withSeries`, not a per-column patch. */
export function withObservedFlow(current: State, observedFlow: DailySeries | null): State {
  return current.phase === "loaded" ? { ...current, observedFlow } : current;
}

/** Records which AEMET station(s) supplied a sub-basin's series after a successful fetch — see AemetFetchDialog.tsx. */
export function withAemetProvenance(
  current: State,
  subBasinCode: number,
  provenance: AemetProvenance
): State {
  return current.phase === "loaded"
    ? { ...current, aemetProvenance: { ...current.aemetProvenance, [subBasinCode]: provenance } }
    : current;
}

/**
 * Uploads or replaces an input series (precipitation/ETP/temperature/
 * pumping) of an already-open project — see SeriesPanel.tsx: needed when
 * the project was loaded without being able to read one (scenario folder
 * not found, moved file...) and, until now, there was no way to fix that
 * without recreating the whole project.
 */
export function withSeries(current: State, series: LoadedProject["series"]): State {
  return current.phase === "loaded"
    ? { ...current, loadedProject: { ...current.loadedProject, series } }
    : current;
}

export function withResultSelection(current: State, selection: ResultSelection): State {
  return current.phase === "loaded" ? { ...current, resultSelection: selection } : current;
}

/**
 * Combines a completed AEMET/ROCIO apply's 3 separate state changes
 * (project/series/one sub-basin's provenance) into a single transition —
 * used by `AemetApplyBuffer` below so a whole burst of applies costs at
 * most one `setState`/re-render instead of 3 per sub-basin. `project`/
 * `series` fully replace (they're already the cumulative result up to this
 * point, see aemetJobStore.ts's own comment on `this.ctx.project`);
 * `provenancePatch` merges (it can hold more than one sub-basin's entry
 * when several applies were buffered together).
 */
export function withAemetApply(
  current: State,
  project: EvalHidProject,
  series: LoadedProject["series"],
  provenancePatch: Record<number, AemetProvenance>
): State {
  return current.phase === "loaded"
    ? {
        ...current,
        loadedProject: { ...current.loadedProject, project, series },
        aemetProvenance: { ...current.aemetProvenance, ...provenancePatch },
      }
    : current;
}

/**
 * How long a burst of AEMET/ROCIO applies gets buffered before actually
 * committing to React state, at most. Bumped 250ms -> 500ms (2026-08-27,
 * reported live): each commit here re-renders the whole tree, including
 * SubBasinMap.tsx's own O(sub-basin count) work (its `signature` string,
 * now memoized — see its own comment — and App.tsx's `validateProject`
 * call) — for a real Duero-scale bulk ROCIO import (1728 sub-basins),
 * Chrome's own long-task warnings showed ~50ms+ per commit, recurring
 * throughout the whole import at the old 250ms cadence. Halving the
 * cadence doesn't reduce the total re-render COST (still O(sub-basins)
 * each), just how often it's paid — the map/badges still update, just a
 * little less often during an already-long background download.
 */
const AEMET_APPLY_THROTTLE_MS = 500;

/**
 * Real scale bug found live: `aemetJobStore.ts` calls `onApplySuccess` once
 * per sub-basin as its download finishes, and applying it used to mean 3
 * separate `setState` calls (project/series/provenance) — for a large
 * MITECO import (the Duero: 1728 sub-basins) that's ~5,000 full top-level
 * re-renders in a row, a real contributor to that scenario's own
 * out-of-memory crash. This buffers a burst of applies and flushes them as
 * ONE `withAemetApply` transition at most every `AEMET_APPLY_THROTTLE_MS` —
 * a plain class (not a hook) so it's unit-testable the same way
 * `AemetJobStore` itself is, independent of React.
 */
export class AemetApplyBuffer {
  private pending: {
    project: EvalHidProject;
    series: LoadedProject["series"];
    provenancePatch: Record<number, AemetProvenance>;
  } | null = null;
  private timeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly commit: (
      project: EvalHidProject,
      series: LoadedProject["series"],
      provenancePatch: Record<number, AemetProvenance>
    ) => void,
    private readonly throttleMs: number = AEMET_APPLY_THROTTLE_MS
  ) {}

  push = (
    subBasinCode: number,
    project: EvalHidProject,
    series: LoadedProject["series"],
    provenance: AemetProvenance
  ): void => {
    this.pending = {
      project,
      series,
      provenancePatch: { ...this.pending?.provenancePatch, [subBasinCode]: provenance },
    };
    if (this.timeout !== null) return;
    this.timeout = setTimeout(() => {
      this.timeout = null;
      this.flush();
    }, this.throttleMs);
  };

  /** Commits whatever's buffered right away — called on the throttle's own timer, and by App.tsx's `onBatchEnd` (before resuming Google Drive sync, so it always syncs the batch's FINAL state, never a stale mid-batch one). A no-op if nothing's pending. */
  flush = (): void => {
    if (this.timeout !== null) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    if (!this.pending) return;
    const { project, series, provenancePatch } = this.pending;
    this.pending = null;
    this.commit(project, series, provenancePatch);
  };
}

export function useProjectDataUpdates(setState: Dispatch<SetStateAction<State>>) {
  const updateProject = useCallback(
    (project: EvalHidProject) => setState((current) => withProject(current, project)),
    [setState]
  );

  const updateGisLayer = useCallback(
    (layer: SubBasinLayer) => setState((current) => withGisLayer(current, layer)),
    [setState]
  );

  const updateOutletPointsLayer = useCallback(
    (layer: OutletPointLayer) => setState((current) => withOutletPointsLayer(current, layer)),
    [setState]
  );

  const updateAquifersLayer = useCallback(
    (layer: AquiferLayer) => setState((current) => withAquifersLayer(current, layer)),
    [setState]
  );

  const updateGenericLayers = useCallback(
    (layers: GenericLayer[]) => setState((current) => withGenericLayers(current, layers)),
    [setState]
  );

  const updateObservedFlow = useCallback(
    (observedFlow: DailySeries | null) =>
      setState((current) => withObservedFlow(current, observedFlow)),
    [setState]
  );

  const recordAemetProvenance = useCallback(
    (subBasinCode: number, provenance: AemetProvenance) =>
      setState((current) => withAemetProvenance(current, subBasinCode, provenance)),
    [setState]
  );

  const updateSeries = useCallback(
    (series: LoadedProject["series"]) => setState((current) => withSeries(current, series)),
    [setState]
  );

  const selectResult = useCallback(
    (selection: ResultSelection) => setState((current) => withResultSelection(current, selection)),
    [setState]
  );

  // One buffer per hook instance (i.e. per App.tsx mount) — not per render,
  // so a throttled flush already scheduled survives across renders. Lazy
  // `useState` initializer (rather than a plain `new` here) so it's only
  // ever constructed once.
  const [aemetApplyBuffer] = useState(
    () =>
      new AemetApplyBuffer((project, series, provenancePatch) =>
        setState((current) => withAemetApply(current, project, series, provenancePatch))
      )
  );

  const onAemetApplySuccess = useCallback(
    (
      subBasinCode: number,
      project: EvalHidProject,
      series: LoadedProject["series"],
      provenance: AemetProvenance
    ) => aemetApplyBuffer.push(subBasinCode, project, series, provenance),
    [aemetApplyBuffer]
  );

  const flushAemetApply = useCallback(() => aemetApplyBuffer.flush(), [aemetApplyBuffer]);

  return {
    updateProject,
    updateGisLayer,
    updateOutletPointsLayer,
    updateAquifersLayer,
    updateGenericLayers,
    updateObservedFlow,
    recordAemetProvenance,
    updateSeries,
    selectResult,
    onAemetApplySuccess,
    flushAemetApply,
  };
}
